// src/components/admin/TorreDeControl/modals/PostponeModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Calendar, Package as PackageIcon } from 'lucide-react';
import axios from 'axios';
import DateSelector from '../DateSelector';

const API_URL = process.env.REACT_APP_API_URL;

function PostponeModal({ item, onClose, onUpdate }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [packageItems, setPackageItems] = useState([]);
  const [isFetchingItems, setIsFetchingItems] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [driverId, setDriverId] = useState(null);

  // Driver assignments by district
  const driver_1 = ["Chorrillos", "San Juan de Miraflores", "Villa El Salvador", "Villa Maria del Triunfo"];
  const driver_2 = ["Comas", "Independencia", "San Martín de Porres", "Los Olivos", "Rímac"];
  const driver_3 = ["Ate", "Santa Anita", "La Molina", "San Luis"];
  const driver_4 = ["Callao", "Jesús María", "Bellavista", "Lince", "Magdalena del Mar", "Carmen de la Legua", "La Perla", "La Punta", "Cercado de Lima", "San Miguel", "Pueblo Libre", "Breña"];
  const driver_5 = ["Puente Piedra", "Carabayllo", "Ventanilla", "Ancon", "Santa Rosa"];
  const driver_6 = ["San Isidro", "San Borja", "Miraflores", "Surquillo", "Santiago de Surco", "Barranco"];
  const driver_7 = ["El Agustino", "La Victoria", "San Juan de Lurigancho"];
  const driver_10 = ["Ayacucho"];
  const driver_11 = ["Arequipa"];
  const driver_12 = ["Juliaca"];
  const driver_13 = ["Puno"];
  const driver_14 = ["Chiclayo"];
  const driver_15 = ["Cusco"];
  const driver_16 = ["Huancayo"];
  const driver_17 = ["Chimbote"];
  const driver_18 = ["Piura"];
  const driver_19 = ["Trujillo"];

  useEffect(() => {
    fetchPackageItems();
  }, [item.id]);

  // Assign driver based on district
  useEffect(() => {
    if (item.district) {
      if (driver_1.includes(item.district)) setDriverId(7);
      else if (driver_2.includes(item.district)) setDriverId(3);
      else if (driver_3.includes(item.district)) setDriverId(6);
      else if (driver_4.includes(item.district)) setDriverId(54);
      else if (driver_5.includes(item.district)) setDriverId(183);
      else if (driver_6.includes(item.district)) setDriverId(164);
      else if (driver_7.includes(item.district)) setDriverId(119);
      else if (driver_10.includes(item.district)) setDriverId(450);
      else if (driver_11.includes(item.district)) setDriverId(451);
      else if (driver_12.includes(item.district)) setDriverId(452);
      else if (driver_13.includes(item.district)) setDriverId(453);
      else if (driver_14.includes(item.district)) setDriverId(454);
      else if (driver_15.includes(item.district)) setDriverId(455);
      else if (driver_16.includes(item.district)) setDriverId(456);
      else if (driver_17.includes(item.district)) setDriverId(458);
      else if (driver_18.includes(item.district)) setDriverId(459);
      else if (driver_19.includes(item.district)) setDriverId(460);
      else setDriverId(null);
    }
  }, [item.district]);

  async function fetchPackageItems() {
    setIsFetchingItems(true);
    setFetchError(null);

    try {
      const response = await axios.get(
        `${API_URL}/api/admin/delivery-point/${item.id}/package-items`
      );
      setPackageItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching package items:', error);
      setFetchError('No se pudieron cargar los productos del paquete');
    } finally {
      setIsFetchingItems(false);
    }
  }

  function formatDateForDisplay(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!selectedDate) {
      alert('Por favor selecciona una fecha');
      return;
    }

    if (packageItems.length === 0) {
      alert('No se encontraron productos para esta entrega');
      return;
    }

    setIsLoading(true);

    try {
      // Prepare data for new delivery with postponement note
      const originalDate = item.delivery_date 
        ? new Date(item.delivery_date).toLocaleDateString('es-PE')
        : 'fecha desconocida';
      
      const postponeNote = `Esta entrega fue pospuesta de la fecha ${originalDate}`;
      const updatedNotes = item.notes 
        ? `${item.notes}\n\n${postponeNote}`
        : postponeNote;

      const newDeliveryData = {
        user_id: item.user_id,
        recipient_name: item.recipient_name,
        recipient_phone: item.recipient_phone,
        recipient_dni: item.recipient_dni,
        address: item.address,
        district: item.district,
        additional_notes: updatedNotes,
        product: '', // Let items array generate the product summary
        charge: item.charge,
        delivery_date: selectedDate,
        provisional_latitude: item.provisional_latitude || item.latitude,
        provisional_longitude: item.provisional_longitude || item.longitude,
        driver_id: driverId, // Assign driver based on district
        items: packageItems, // Send the SKUs and quantities
      };

      // Create new delivery
      await axios.post(`${API_URL}/api/entrega`, newDeliveryData);
      
      // Update original delivery status to 'postergado'
      await axios.put(`${API_URL}/api/admin/delivery-point/${item.id}/status`, {
        status: 'postergado',
      });

      alert(`Entrega pospuesta exitosamente para el ${formatDateForDisplay(selectedDate)}`);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error postponing delivery:', error);
      const errorMsg = error.response?.data?.error || 'Error al posponer entrega';
      alert(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        <div className="bg-[#B1336E] px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-bold text-lg flex items-center gap-2 font-montserrat">
            <Calendar className="w-5 h-5" />
            Posponer Entrega
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Current Delivery Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 font-montserrat">
              <strong>Entrega actual:</strong> #{item.id}
            </p>
            <p className="text-sm text-blue-800 font-montserrat mt-1">
              <strong>Fecha actual:</strong>{' '}
              {item.delivery_date
                ? new Date(item.delivery_date).toLocaleDateString('es-PE')
                : 'N/A'}
            </p>
            <p className="text-sm text-blue-800 font-montserrat mt-1">
              <strong>Destinatario:</strong> {item.recipient_name}
            </p>
            {item.district && (
              <p className="text-sm text-blue-800 font-montserrat mt-1">
                <strong>Distrito:</strong> {item.district}
              </p>
            )}
            {driverId && (
              <p className="text-sm text-blue-800 font-montserrat mt-1">
                <strong>Motorizado asignado:</strong> ID {driverId}
              </p>
            )}
          </div>

          {/* Package Items Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <PackageIcon className="w-4 h-4 text-slate-600" />
              <h4 className="text-sm font-bold text-slate-800 font-montserrat">
                Productos a posponer
              </h4>
            </div>
            
            {isFetchingItems ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#B1336E]"></div>
                <span className="ml-2 text-sm text-slate-600 font-montserrat">
                  Cargando productos...
                </span>
              </div>
            ) : fetchError ? (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-sm text-red-800 font-montserrat">{fetchError}</p>
              </div>
            ) : packageItems.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm text-yellow-800 font-montserrat">
                  No se encontraron productos para esta entrega
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {packageItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center text-sm bg-white rounded border border-slate-200 p-2"
                  >
                    <span className="text-slate-700 font-montserrat">
                      SKU: {item.sku}
                    </span>
                    <span className="text-slate-600 font-montserrat">
                      Cantidad: {item.quantity}
                    </span>
                  </div>
                ))}
                <p className="text-xs text-slate-500 font-montserrat mt-2">
                  Total: {packageItems.reduce((sum, item) => sum + item.quantity, 0)} unidad(es)
                </p>
              </div>
            )}
          </div>

          {/* Date Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
              Nueva fecha de entrega
            </label>
            <DateSelector
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              showCalendar={showCalendar}
              onToggleCalendar={() => setShowCalendar(!showCalendar)}
            />
          </div>

          {/* Warning Note */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-xs text-yellow-800 font-montserrat">
              <strong>Nota:</strong> Se creará una nueva entrega para la fecha seleccionada
              con los mismos productos{driverId ? ` y será asignada al motorizado ID ${driverId}` : ''}, 
              y la entrega actual se marcará como "postergada".
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || !selectedDate || packageItems.length === 0 || isFetchingItems}
              className="flex-1 bg-[#B1336E] hover:bg-[#B1336E]/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              {isLoading ? 'Posponiendo...' : 'Posponer Entrega'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PostponeModal;